import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { filter, flatMap, keyBy } from "lodash";

import { IUnit } from "@/api";

// Types for hierarchy
interface IHierarchyNode {
  id: string;
  name: string;
  parentId?: string;
  type: "market" | "facility" | "unit";
  level: number;
  unitId?: string;
}

interface INodeSelectionState {
  allSelected: boolean;
  someSelected: boolean;
  unitIds: string[];
}

export const useHandleQueryParams = ({
  onChange,
  valueIds,
  withQueryParams = false,
  nodeSelectionState,
  hierarchyNodes,
  options,
  isLoading,
  allUnitsMap,
}: {
  onChange: (selectedUnits: IUnit[]) => void;
  valueIds: IUnit["id"][];
  withQueryParams?: boolean;
  nodeSelectionState: Record<string, INodeSelectionState>;
  hierarchyNodes: IHierarchyNode[];
  options: { value: IUnit["id"]; label: string }[];
  isLoading: boolean;
  allUnitsMap: Map<string, IUnit>;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedUnitsAndNodesFromParams = searchParams.get("units");
  const valuesSetFromParams = useRef(false);

  // call onChange from values from query params
  useEffect(() => {
    const optionsByLabel = keyBy(options, "label");
    const nodesById = keyBy(hierarchyNodes, "id");

    if (!withQueryParams) return;
    if (valuesSetFromParams.current) return;
    if (isLoading || !allUnitsMap.size) return;

    valuesSetFromParams.current = true;
    if (selectedUnitsAndNodesFromParams) {
      const idsFromQuery = flatMap(
        (selectedUnitsAndNodesFromParams || "").split(",").map((nodeOrUnit) => {
          // Check if it's a node ID (market or facility)
          const node = nodesById[nodeOrUnit];
          if (node) {
            // If it's a node, get all its unit IDs
            return nodeSelectionState[node.id]?.unitIds || [];
          }
          // Otherwise, it's a unit name - try to find the unit ID
          return [optionsByLabel[nodeOrUnit]?.value].filter(Boolean);
        }),
      );
      onChange(Array.from(allUnitsMap.values()).filter((unit) => idsFromQuery.includes(unit.id)));
    }
  }, [
    isLoading,
    withQueryParams,
    allUnitsMap,
    nodeSelectionState,
    onChange,
    options,
    selectedUnitsAndNodesFromParams,
    valueIds.length,
    hierarchyNodes,
  ]);

  // Change query params when values change
  useEffect(() => {
    if (!withQueryParams) return;
    if (!valuesSetFromParams.current) return;

    // Find fully selected markets and facilities
    const fullySelectedNodes = hierarchyNodes.filter(
      (node) => node.type !== "unit" && nodeSelectionState[node.id]?.allSelected,
    );

    // Find partially selected units (not part of a fully selected node)
    const unitIdsInFullySelectedNodes = flatMap(
      fullySelectedNodes,
      (node) => nodeSelectionState[node.id]?.unitIds || [],
    );

    const unitNamesToKeep: string[] = [];
    options.forEach(({ value, label }) => {
      if (valueIds.includes(value) && !unitIdsInFullySelectedNodes.includes(value)) {
        unitNamesToKeep.push(label);
      }
    });

    // Combine node IDs and unit names
    const unitsQueryValue = filter([
      ...fullySelectedNodes.map((node) => node.id),
      ...unitNamesToKeep,
    ]).join(",");

    setSearchParams((otherParams) => {
      unitsQueryValue ? otherParams.set("units", unitsQueryValue) : otherParams.delete("units");
      return otherParams;
    });
  }, [nodeSelectionState, hierarchyNodes, options, setSearchParams, valueIds, withQueryParams]);

  return valuesSetFromParams;
};
